import { useState } from 'react';

export default function GetHelpSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.service) {
      setSubmitMessage('Please fill in all required fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitMessage('Please enter a valid email address.');
      return;
    }
    if (formData.message.length > 500) {
      setSubmitMessage('Message must be 500 characters or less.');
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const encoded = new URLSearchParams();
      encoded.append('name', formData.name);
      encoded.append('email', formData.email);
      encoded.append('phone', formData.phone);
      encoded.append('service', formData.service);
      encoded.append('message', formData.message);
      const response = await fetch('https://readdy.ai/api/form/d77pb355uau42b5indc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encoded.toString(),
      });
      if (response.ok) {
        setSubmitMessage('Thank you! A support navigator will reach out within 24–48 hours.');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="get-help" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-teal-500"></div>
              <span className="text-sm font-semibold text-teal-600 uppercase tracking-wider">Take the First Step</span>
            </div>
            <h3
              className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Request Support — We'll Respond in 24–48 Hours
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Fill out this short form and a dedicated support navigator will reach out to you personally. No judgment, no complicated process — just real help from real people.
            </p>

            <div className="space-y-5">
              {[
                { icon: 'ri-shield-check-line', title: 'Confidential & Safe', desc: 'Your information is private and protected.' },
                { icon: 'ri-timer-flash-line', title: 'Fast Response', desc: 'We respond within 24–48 hours, guaranteed.' },
                { icon: 'ri-global-line', title: 'Culturally Sensitive', desc: 'We speak your language and understand your background.' },
                { icon: 'ri-price-tag-3-line', title: 'Free Services', desc: 'Our support services are available at no cost.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-xl text-teal-600`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{item.title}</div>
                    <div className="text-gray-600 text-sm">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100">
            <h4 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Intake Request Form
            </h4>
            <p className="text-gray-500 text-sm mb-8">All fields marked * are required.</p>

            <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="intake-form">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(123) 456-7890"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">Type of Support Needed *</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm bg-white"
                  required
                >
                  <option value="">Select a service...</option>
                  <option value="Housing Support">Housing Support</option>
                  <option value="Nutrition & Basic Needs">Nutrition &amp; Basic Needs</option>
                  <option value="Mental Health Support">Mental Health Support</option>
                  <option value="Family & Youth Services">Family &amp; Youth Services</option>
                  <option value="Skills Development">Skills Development</option>
                  <option value="Home & Caregiving Support">Home &amp; Caregiving Support</option>
                  <option value="General Navigation">General Navigation / Not Sure</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Brief Description <span className="text-gray-400 font-normal">(Max 500 characters)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder="Tell us a little about your situation and what kind of help you're looking for..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                ></textarea>
                <div className="text-xs text-gray-400 text-right mt-1">{formData.message.length}/500</div>
              </div>

              {submitMessage && (
                <div className={`p-4 rounded-xl text-sm ${submitMessage.includes('Thank you') ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {submitMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-base"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Submit Request
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
